import styled from '@emotion/styled';
import { layout, palette, radius, shadow, spacing, typography } from '../../../../style-utils/theme';
import { up } from '../../../../style-utils/media-queries';

/**
 * THE STAYS SCREENS' OWN PIECES (ai-plans/stays plan S1).
 *
 * The register shape is the visitor-pass register's (dashboard-passes.styled):
 * its Screen / Lead / Toolbar / Empty / Footer pieces are re-exported here
 * rather than copied, so the two registers cannot drift apart. What is below is
 * only what Stays adds.
 */
export {
  Empty,
  EmptyText,
  EmptyTitle,
  Footer,
  FooterNote,
  Lead,
  NameButton,
  RowActions,
  Screen,
  Sub,
  Toolbar,
  ToolbarField,
  ToolbarLabel,
  SwitchCard,
  SwitchHint,
} from '../dashboard-passes-components/dashboard-passes.styled';

/* A status pill and its "Needs attention" pill side by side; wraps on a phone. */
export const Pills = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
  min-width: 0;
`;

/* The time pair "15:00 / 11:00": tabular figures so a column of them aligns. */
export const Times = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const EmptyActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.sm};
`;

/* The listing page: its header block. */
export const DetailHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.sm};
  min-width: 0;
`;

export const DetailTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  flex: 1 1 ${spacing.huge};
`;

export const DetailTitle = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const DetailSub = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* The listing basics as a definition list: label above value on a phone,
   two columns from md. */
export const SpecGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm} ${spacing.md};
  margin: 0;

  ${up('md')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const SpecItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
`;

export const SpecLabel = styled.dt`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

export const SpecValue = styled.dd`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

/* design_system §9 Cards: surface, radius.md, shadow.sm, 24px (16px on a phone). */
export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};
  min-width: 0;

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const CardTitle = styled.h3`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
`;

export const RetiredLine = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

/* The attention banner — a notice, not an error: warning tones. */
export const AttentionBanner = styled.div`
  display: flex;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${radius.md};
  border: 1px solid ${palette.warning};
  background: ${palette.warningSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
`;

export const FormAlert = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.alertStrong};
`;

/* A toolbar control with no label of its own (the status switch): a box as
   tall as the select beside it, so the 44px switch sits centred on the 48px
   select instead of on its bottom edge. */
export const ToolbarControl = styled.div`
  display: flex;
  align-items: center;
  min-height: ${layout.fieldHeight};
  min-width: 0;
`;

/* A value the form shows but does not let the reader change (the listing's
   country, which is the building's). */
export const ReadValue = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;
